import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import { articles } from '@/mocks/articles';

const articleHeaderImages: Record<string, string> = {
  'wynn-palace-suite-booking-guide': 'https://readdy.ai/api/search-image?query=Luxury%20Macau%20hotel%20suite%20with%20cream%20silk%20bedding%20and%20golden%20accents%2C%20floor%20to%20ceiling%20windows%20with%20city%20skyline%20view%2C%20elegant%20warm%20lighting%2C%20asian%20luxury%20interior%2C%20stylized%20editorial%20photography&width=1400&height=600&seq=article-header-hotel-01&orientation=landscape',
  'wynn-palace-michelin-restaurants': 'https://readdy.ai/api/search-image?query=Michelin%20star%20restaurant%20exquisite%20dish%20presentation%20with%20warm%20golden%20lighting%2C%20crystal%20chandelier%20background%2C%20fine%20dining%20atmosphere%2C%20asian%20luxury%20aesthetic%2C%20stylized%20food%20photography&width=1400&height=600&seq=article-header-dining-01&orientation=landscape',
  'wynn-palace-performance-lake-show': 'https://readdy.ai/api/search-image?query=Magnificent%20night%20water%20fountain%20show%20with%20colorful%20illuminated%20jets%20and%20fire%20effects%2C%20grand%20luxury%20building%20backdrop%2C%20stylized%20cinematic%20photography%2C%20dramatic%20lighting&width=1400&height=600&seq=article-header-entertainment-01&orientation=landscape',
  'wynn-palace-luxury-shopping': 'https://readdy.ai/api/search-image?query=Luxury%20shopping%20mall%20interior%20with%20marble%20floors%20and%20golden%20accents%2C%20high%20end%20brand%20boutiques%2C%20elegant%20warm%20lighting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20editorial%20photography&width=1400&height=600&seq=article-header-shopping-01&orientation=landscape',
  'macau-three-day-itinerary': 'https://readdy.ai/api/search-image?query=Historic%20Macau%20cityscape%20with%20ruins%20of%20Saint%20Paul%20and%20Portuguese%20architecture%2C%20warm%20sunset%20golden%20light%2C%20cobblestone%20streets%2C%20stylized%20travel%20photography%2C%20soft%20atmospheric%20haze&width=1400&height=600&seq=article-header-travel-01&orientation=landscape',
  'wynn-palace-spa-experience': 'https://readdy.ai/api/search-image?query=Luxury%20spa%20treatment%20room%20with%20soft%20warm%20candlelight%2C%20cream%20towels%20and%20orchid%20flowers%2C%20serene%20atmosphere%2C%20asian%20wellness%20aesthetic%2C%20stylized%20editorial%20photography%2C%20clean%20composition&width=1400&height=600&seq=article-header-spa-01&orientation=landscape',
  'wynn-palace-family-friendly': 'https://readdy.ai/api/search-image?query=Family%20friendly%20luxury%20hotel%20pool%20area%20with%20children%20water%20slides%2C%20warm%20sunny%20day%2C%20tropical%20landscape%2C%20asian%20luxury%20resort%2C%20stylized%20editorial%20photography%2C%20bright%20natural%20light&width=1400&height=600&seq=article-header-family-01&orientation=landscape',
  'wynn-palace-wedding-venue': 'https://readdy.ai/api/search-image?query=Elegant%20luxury%20wedding%20ballroom%20setup%20with%20golden%20decorations%20and%20cream%20flowers%2C%20crystal%20chandeliers%2C%20romantic%20warm%20lighting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20editorial%20photography&width=1400&height=600&seq=article-header-wedding-01&orientation=landscape',
};

const relatedArticleImages: Record<string, string> = {
  'wynn-palace-suite-booking-guide': 'https://readdy.ai/api/search-image?query=Luxury%20Macau%20hotel%20suite%20bedroom%20with%20cream%20silk%20bedding%2C%20elegant%20warm%20golden%20lighting%2C%20asian%20luxury%20interior%20design%2C%20stylized%20editorial%20photography&width=600&height=375&seq=article-hotel-01-related&orientation=landscape',
  'wynn-palace-michelin-restaurants': 'https://readdy.ai/api/search-image?query=Michelin%20star%20restaurant%20exquisite%20dish%20presentation%20with%20golden%20chandelier%2C%20fine%20dining%20atmosphere%2C%20warm%20elegant%20lighting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20food%20photography&width=600&height=375&seq=article-dining-01-related&orientation=landscape',
  'wynn-palace-performance-lake-show': 'https://readdy.ai/api/search-image?query=Magnificent%20night%20water%20fountain%20show%20with%20colorful%20illuminated%20jets%20and%20fire%20effects%2C%20grand%20luxury%20building%20backdrop%2C%20stylized%20cinematic%20photography%2C%20dramatic%20lighting&width=600&height=375&seq=article-entertainment-01-related&orientation=landscape',
  'wynn-palace-luxury-shopping': 'https://readdy.ai/api/search-image?query=Luxury%20boutique%20storefront%20with%20golden%20warm%20lighting%2C%20marble%20floor%20reflections%2C%20high%20end%20fashion%20display%2C%20elegant%20mall%20interior%2C%20asian%20luxury%20aesthetic&width=600&height=375&seq=article-shopping-01-related&orientation=landscape',
  'macau-three-day-itinerary': 'https://readdy.ai/api/search-image?query=Historic%20Macau%20streets%20with%20Portuguese%20architecture%20and%20warm%20sunset%20light%2C%20colorful%20colonial%20buildings%2C%20cobblestone%20path%2C%20stylized%20travel%20photography%2C%20soft%20atmospheric%20haze&width=600&height=375&seq=article-travel-01-related&orientation=landscape',
  'wynn-palace-spa-experience': 'https://readdy.ai/api/search-image?query=Luxury%20spa%20treatment%20room%20with%20soft%20warm%20candlelight%2C%20cream%20towels%20and%20orchid%20flowers%2C%20serene%20atmosphere%2C%20asian%20wellness%20aesthetic%2C%20stylized%20editorial%20photography&width=600&height=375&seq=article-spa-01-related&orientation=landscape',
  'wynn-palace-family-friendly': 'https://readdy.ai/api/search-image?query=Family%20friendly%20luxury%20hotel%20pool%20area%20with%20children%20water%20slides%2C%20warm%20sunny%20day%2C%20tropical%20landscape%2C%20asian%20luxury%20resort%2C%20stylized%20editorial%20photography&width=600&height=375&seq=article-family-01-related&orientation=landscape',
  'wynn-palace-wedding-venue': 'https://readdy.ai/api/search-image?query=Elegant%20luxury%20wedding%20ballroom%20setup%20with%20golden%20decorations%20and%20cream%20flowers%2C%20crystal%20chandeliers%2C%20romantic%20warm%20lighting%2C%20asian%20luxury%20aesthetic&width=600&height=375&seq=article-wedding-01-related&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: articleHeaderImages[article.slug] || '',
    publisher: {
      '@type': 'Organization',
      name: '永利皇宫·官方体验指南',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://embedded-expo.cn/article/${slug}`}
        ogType="article"
        ogImage={articleHeaderImages[article.slug]}
        schema={[articleSchema]}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[340px] md:h-[440px] flex items-end overflow-hidden">
          <img
            src={articleHeaderImages[article.slug] || ''}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-10">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: article.categoryName, to: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>
        </section>

        <article className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-foreground-400 text-xs">{article.readTime} 阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-foreground-500 text-sm">
                <div className="flex items-center gap-1.5">
                  <i className="ri-user-line text-sm"></i>
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-sm"></i>
                  <span>{article.publishDate}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-lg bg-secondary-100 text-secondary-600 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h2:font-heading prose-h2:text-xl prose-h2:font-semibold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
                prose-h3:font-heading prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
                prose-li:text-foreground-700 prose-li:my-1
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-ul:my-4 prose-ul:pl-5"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-secondary-200/60">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group rounded-xl bg-background-100 border border-secondary-200/60 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={relatedArticleImages[related.slug] || relatedArticleImages['wynn-palace-suite-booking-guide']}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-foreground-400 text-xs">{related.categoryName}</span>
                        <h4 className="font-heading text-sm font-medium text-foreground-900 mt-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}